import get from 'lodash/get'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type { State } from 'types/redux'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import { getCampaignStatsDiff } from '@disco/utils/emailCampaigns'
import Card from 'components/common/dashboard/card'
import TrackList from 'components/common/dashboard/card-content/tracks-list'
import UserList from 'components/common/dashboard/card-content/users-list'
import CircleIndicator from 'components/common/dashboard/circle-indicator'
import IndicatorLegend from 'components/common/dashboard/indicator-legend'
import Row from 'components/common/row'
import Counter from 'components/ui/counter'
import DataTable from 'components/ui/data-table'
import Delta from 'components/ui/delta'
import type { Column } from 'components/ui/data-table'
type OwnProps = {
  campaign: EmailCampaign | null | undefined
  campaignId: string
}
const dashboardColumns = {
  c3_3_6: [3, 3, 6],
  c4_4_4: [4, 4, 4],
  c6_6: [6, 6],
  c12: [12],
}
const EMPTY_ARRAY = []
const MOST_CLICKED_LINKS_COLUMNS: Column[] = [
  {
    id: 'link',
  },
  {
    id: 'clicks',
    width: 'content',
  },
]

const mapStateToProps = (state: State, { campaignId }: OwnProps) => ({
  campaign: getCampaign(state, {
    campaignId: parseInt(campaignId, 10),
  }),
})

type Props = OwnProps & {
  children?: React.ReactNode
}

const CampaignDashboard = ({ campaign, children }: Props) => {
  if (!campaign) return null
  const openRate = get(campaign.stats, 'open_rate', 0)
  const clickRate = get(campaign.stats, 'click_rate', 0)
  const openRateChange = getCampaignStatsDiff(campaign.stats, 'open_rate')
  const openRateDiffText = `${Math.abs(openRateChange)}% ${
    openRateChange >= 0 ? 'more' : 'less'
  } than last campaign`
  const clickRateChange = getCampaignStatsDiff(campaign.stats, 'click_rate')
  const clickRateDiffText = `${Math.abs(clickRateChange)}% ${
    clickRateChange >= 0 ? 'more' : 'less'
  } than last campaign`
  const streams = get(campaign.stats, 'most_streamed_tracks', EMPTY_ARRAY)
  const downloads = get(campaign.stats, 'most_downloaded_tracks', EMPTY_ARRAY)
  const links = get(campaign.stats, 'most_clicked_links', EMPTY_ARRAY)
  const playlistsDiff = getCampaignStatsDiff(
    campaign.stats,
    'total_playlist_downloads',
  )
  const streamsDiff = getCampaignStatsDiff(campaign.stats, 'total_streams')
  const tracksDiff = getCampaignStatsDiff(
    campaign.stats,
    'total_track_downloads',
  )
  return (
    <>
      <Row columnsSize={dashboardColumns.c3_3_6}>
        <div>
          <Card
            title="Open rate"
            footer={
              openRateChange !== 0 ? (
                <Delta
                  type={openRateChange > 0 ? 'positive' : 'negative'}
                  value={openRateDiffText}
                />
              ) : (
                'No change'
              )
            }
          >
            <IndicatorLegend
              value={get(campaign.stats, 'number_of_opens', '0')}
              valueText="total email opens"
            />
            <CircleIndicator
              value={openRate}
              valueSuffix="%"
              percentage={openRate}
            />
          </Card>
        </div>
        <div>
          <Card
            title="Click rate"
            footer={
              clickRateChange !== 0 ? (
                <Delta
                  type={clickRateChange > 0 ? 'positive' : 'negative'}
                  value={clickRateDiffText}
                />
              ) : (
                'No change'
              )
            }
          >
            <IndicatorLegend
              value={get(campaign.stats, 'number_of_clicks', '0')}
              valueText="total link clicks"
            />
            <CircleIndicator
              value={clickRate}
              valueSuffix="%"
              percentage={clickRate}
            />
          </Card>
        </div>
        <div>
          <Card
            title="Most engaged users"
            footer={
              <Link to={`/campaigns/${campaign.id}/stats/users`}>View all</Link>
            }
          >
            <UserList
              compact
              users={get(
                campaign.stats,
                'most_engaged_users',
                EMPTY_ARRAY,
              ).slice(0, 5)}
            />
          </Card>
        </div>
      </Row>
      {(streams.length > 0 || downloads.length > 0) && (
        <Row columnsSize={dashboardColumns.c6_6}>
          <div>
            <Card title="Most streamed tracks">
              <TrackList type="streams" tracks={streams.slice(0, 5)} />
            </Card>
          </div>
          <div>
            <Card title="Most downloaded tracks">
              <TrackList type="downloads" tracks={downloads.slice(0, 5)} />
            </Card>
          </div>
        </Row>
      )}
      <Row columnsSize={dashboardColumns.c4_4_4}>
        <div>
          <Card title="Playlist downloads" icon="file-download">
            <Counter
              value={get(campaign.stats, 'total_playlist_downloads', 0)}
              delta={playlistsDiff}
            />
          </Card>
        </div>
        <div>
          <Card title="Total streams" icon="music-note">
            <Counter
              value={get(campaign.stats, 'total_streams', 0)}
              delta={streamsDiff}
            />
          </Card>
        </div>
        <div>
          <Card title="Total track downloads" icon="file-download">
            <Counter
              value={get(campaign.stats, 'total_track_downloads', 0)}
              delta={tracksDiff}
            />
          </Card>
        </div>
      </Row>

      {links.length > 0 && (
        <Row columnsSize={dashboardColumns.c12}>
          <div>
            <Card title="Most clicked links">
              <DataTable
                appearance="plain"
                columns={MOST_CLICKED_LINKS_COLUMNS}
                data={links}
              />
            </Card>
          </div>
        </Row>
      )}
      {children}
    </>
  )
}

export default connect(mapStateToProps, {})(CampaignDashboard) as any
